import { ADVISORS } from "../state/schema";
import {
  card, checkbox, h, moneyInput, note, optionCards, slider, textareaInput,
} from "../components/ui";
import { uploadBlock } from "../components/upload";
import { advisorLabel } from "../logic/advisors";
import { berechneHaushalt } from "../logic/calc";
import { formatEUR } from "../logic/format";
import {
  investitionsWarnung, vBerater, vEinwilligungen, vPraeferenzen, vZukunft,
} from "../logic/validation";
import { Step } from "./types";

/* ---------- 15 · Zukunft & Investitionsrahmen ---------- */
export const stepZukunft: Step = {
  id: "zukunft",
  section: "zukunft",
  eyebrow: "Zukunft",
  title: "Deine Zukunft im Visier",
  subtitle: "Was möchtest du künftig für dich einplanen?",
  leaveMessage: "Fast geschafft – jetzt deine Zukunft.",
  validate: vZukunft,
  build(ctx) {
    const s = ctx.store;
    const hh = berechneHaushalt(s.state);
    const frei = hh.freiVerfuegbar;
    const empfMin = Math.round(frei * 0.5);
    const empfMax = Math.round(frei * 0.8);

    const warnBox = h("div");
    const updateWarn = () => {
      warnBox.innerHTML = "";
      const w = investitionsWarnung(s.state);
      if (w) warnBox.appendChild(note("warn", w));
    };

    const kv = (k: string, v: string) =>
      h("div", { class: "kv__row" },
        h("span", { class: "kv__k", style: { color: "var(--on-card-muted)" }, text: k }),
        h("span", { class: "kv__v", style: { color: "var(--on-card)" }, text: v }));

    const block = card(
      h("div", { class: "card__title", text: "Aus deiner Analyse übernommen" }),
      h("div", { class: "kv" },
        kv("Einnahmen gesamt", formatEUR(hh.einnahmenGesamt)),
        kv("Laufende Verbindlichkeiten", formatEUR(hh.laufendeVerbindlichkeiten)),
        kv(frei < 0 ? "Monatliche Unterdeckung" : "Frei verfügbar", formatEUR(frei))),
      h("p", { class: "field__optional", text: "Diese Werte stammen aus deinen vorherigen Angaben und müssen hier nicht erneut eingegeben werden." })
    );

    const empfehlung = frei > 0
      ? card(
          h("div", { class: "card__title", text: "★ Unsere Experten-Empfehlung" }),
          h("p", { class: "card__hint", text: `Aus deinem frei verfügbaren Betrag von ${formatEUR(frei)} empfehlen wir, monatlich zwischen ${formatEUR(empfMin)} (50 %) und ${formatEUR(empfMax)} (80 %) für deine Zukunft einzuplanen.` }),
          note("info-dark", "Je näher du an dieser Empfehlung bleibst, desto schneller erreichst du deine Ziele und Träume. Die Werte sind ein Vorschlag – du kannst sie jederzeit anpassen."),
          h("button", { type: "button", class: "btn-add", text: "Empfehlung übernehmen", on: { click: () => { s.set("zukunft.mindestens", String(empfMin)); s.set("zukunft.komfortabel", String(empfMax)); ctx.rerender(); } } })
        )
      : note("info-dark", "Aktuell ist kein frei verfügbarer Betrag vorhanden – das ist kein Problem. Dein Berater zeigt dir, wo Spielraum für deine Zukunft entsteht.");

    const input = card(
      h("div", { class: "card__title", text: "Welchen Betrag möchtest du künftig für deine finanzielle Zukunft einplanen?" }),
      moneyInput(s, "zukunft.mindestens", { label: "Mindestens (monatlich)", required: true, hint: frei > 0 ? `Empfehlung: ${formatEUR(empfMin)} (50 % deines frei verfügbaren Betrags).` : undefined, onInput: updateWarn }),
      moneyInput(s, "zukunft.komfortabel", { label: "Maximal (monatlich)", required: true, hint: frei > 0 ? `Empfehlung: ${formatEUR(empfMax)} (80 % deines frei verfügbaren Betrags).` : "Was du dir maximal vorstellen kannst, für deine Ziele und Träume zur Seite zu legen.", onInput: updateWarn }),
      moneyInput(s, "zukunft.einmalig", { label: "Einmalig verfügbarer Anlagebetrag", required: true, hint: "Auch 0 € ist möglich." }),
      warnBox
    );
    updateWarn();
    return h("div", {}, block, empfehlung, input);
  },
};

/* ---------- 16 · Verantwortung, Risiko, Flexibilität, Präferenz ---------- */
export const stepPraeferenzen: Step = {
  id: "praeferenzen",
  section: "zukunft",
  eyebrow: "Zukunft",
  title: "Deine Haltung zu Investments",
  subtitle: "Es gibt keine richtigen oder falschen Antworten.",
  validate: vPraeferenzen,
  build(ctx) {
    const s = ctx.store;

    const risikoText = (v: number) =>
      v <= 3 ? "Sicherheit ist dir besonders wichtig. Größere Schwankungen möchtest du eher vermeiden."
        : v <= 6 ? "Du kannst gewisse Schwankungen akzeptieren, wenn langfristig bessere Chancen bestehen."
          : "Du kannst auch deutlichere Schwankungen akzeptieren und langfristig ruhig bleiben.";
    const flexText = (v: number) =>
      v <= 3 ? "Du kannst dir vorstellen, Geld langfristig einzuplanen."
        : v <= 6 ? "Dir ist eine ausgewogene Mischung aus Planung und Flexibilität wichtig."
          : "Du möchtest möglichst flexibel bleiben und schnell auf dein Geld zugreifen können.";

    return h("div", {},
      card(
        h("div", { class: "card__title", text: "Fühlst du dich bereit, die volle Verantwortung und sämtliche Risiken deiner Investmententscheidungen selbst zu tragen – oder möchtest du Verantwortung lieber abgeben?" }),
        optionCards(s, "verantwortung", [
          { value: "selbst", title: "Ich entscheide selbst", desc: "Ich möchte meine Entscheidungen selbst treffen und die damit verbundenen Risiken selbst tragen." },
          { value: "unterstuetzung", title: "Ich möchte Unterstützung", desc: "Ich möchte Entscheidungen gemeinsam mit professioneller Unterstützung treffen." },
          { value: "abgeben", title: "Ich möchte Verantwortung abgeben", desc: "Ich möchte Entscheidungen und die damit verbundenen Risiken nicht allein tragen müssen." },
        ])
      ),
      card(
        h("div", { class: "card__title", text: "Wie gut kannst du damit umgehen, wenn der Wert deiner Geldanlage vorübergehend fällt?" }),
        slider(s, "risiko", {
          left: "1 – sehr unwohl", mid: "Risikobereitschaft",
          right: "10 – hält viel aus",
        }, risikoText)
      ),
      card(
        h("div", { class: "card__title", text: "Wie wichtig ist es dir, jederzeit auf dein angelegtes Geld zugreifen zu können?" }),
        slider(s, "flexibilitaet", {
          left: "1 – langfristig ok", mid: "Flexibilität",
          right: "10 – jederzeit",
        }, flexText)
      ),
      card(
        h("div", { class: "card__title", html: "Was wäre für dich am schwersten zu akzeptieren? <span style='color:var(--neg)'>*</span>" }),
        optionCards(s, "praeferenz", [
          { value: "kurzfristige_verluste", title: "Kurzfristige Verluste" },
          { value: "geringe_ertraege", title: "Langfristig zu geringe Erträge" },
          { value: "verfuegbarkeit", title: "Eingeschränkte Verfügbarkeit" },
          { value: "falsche_entscheidung", title: "Selbst eine falsche Entscheidung getroffen zu haben" },
        ])
      )
    );
  },
};

/* ---------- 17 · Dokumente & Checkliste ---------- */
export const stepDokumente: Step = {
  id: "dokumente",
  section: "abschluss",
  eyebrow: "Abschluss",
  title: "Dokumente",
  subtitle: "Lade hoch, was du griffbereit hast.",
  build(ctx) {
    const s = ctx.store;
    const checklist = h("div", { class: "checklist" });
    const renderChecklist = () => {
      checklist.innerHTML = "";
      const items: { label: string; kat: string; pflicht: boolean }[] = [
        { label: "Einkommensnachweis", kat: "Einkommensnachweis", pflicht: s.state.einkommensnachweis.art === "gehaltsabrechnung" || s.state.einkommensnachweis.art === "jahresendabrechnung" },
        { label: "Versicherungsscheine", kat: "Sachversicherung", pflicht: false },
        { label: "Stromabrechnung", kat: "Stromabrechnung", pflicht: false },
        { label: "Gasabrechnung", kat: "Gasabrechnung", pflicht: false },
        { label: "Personalausweis", kat: "Personalausweis Vorderseite", pflicht: false },
      ];
      for (const it of items) {
        const docs = s.state.dokumente.filter((d) => d.kategorie === it.kat);
        const hasErr = docs.some((d) => d.status === "error");
        const hasDone = docs.some((d) => d.status === "done");
        let badge = it.pflicht ? "badge--open" : "badge--na";
        let txt = it.pflicht ? "noch offen" : "optional";
        if (hasDone) { badge = "badge--done"; txt = "hochgeladen"; }
        else if (hasErr) { badge = "badge--err"; txt = "fehlgeschlagen"; }
        else if (!it.pflicht && docs.length === 0) { badge = "badge--na"; txt = "nicht erforderlich"; }
        checklist.appendChild(h("div", { class: "checkitem" },
          h("span", { class: `checkitem__badge ${badge}`, text: txt }),
          h("span", { text: it.label })));
      }
    };
    const refresh = () => renderChecklist();
    renderChecklist();

    return h("div", {},
      card(h("div", { class: "card__title", text: "Dokumenten-Checkliste" }), checklist),
      card(
        h("div", { class: "card__title", text: "Personalausweis (optional)" }),
        note("info", "Optional – Vorder- und Rückseite helfen uns bei einer späteren Identifikation und Antragserstellung. Du kannst die Mehrwertanalyse auch ohne Ausweiskopie abschließen."),
        h("div", { class: "field__label", text: "Vorderseite" }),
        uploadBlock(s, { kategorie: "Personalausweis Vorderseite", multiple: false, onChange: refresh }),
        h("div", { class: "field__label", style: { marginTop: "12px" }, text: "Rückseite" }),
        uploadBlock(s, { kategorie: "Personalausweis Rückseite", multiple: false, onChange: refresh })
      ),
      card(
        h("div", { class: "card__title", text: "Sonstige relevante Unterlage (optional)" }),
        uploadBlock(s, { kategorie: "Sonstige relevante Unterlage", onChange: refresh })
      )
    );
  },
};

/* ---------- 18 · Sonstige Hinweise ---------- */
export const stepSonstiges: Step = {
  id: "sonstiges",
  section: "abschluss",
  eyebrow: "Abschluss",
  title: "Sonstige Hinweise",
  subtitle: "Gibt es noch etwas, das wir wissen sollten?",
  build(ctx) {
    return card(
      textareaInput(ctx.store, "sonstiges", {
        label: "Gibt es noch etwas, das wir vor deiner nächsten Beratung wissen sollten?",
        optional: true, optionalText: "Optional – nur ausfüllen, wenn du uns noch etwas mitteilen möchtest.",
        rows: 5, maxlength: 1500, placeholder: "Deine Nachricht …",
      })
    );
  },
};

/* ---------- 19 · Beraterauswahl ---------- */
export const stepBerater: Step = {
  id: "berater",
  section: "abschluss",
  eyebrow: "Abschluss",
  title: "Dein persönlicher Berater",
  subtitle: "Wer betreut dich?",
  validate: vBerater,
  build(ctx) {
    return card(
      h("div", { class: "card__title", html: "Wer ist dein persönlicher Berater? <span style='color:var(--neg)'>*</span>" }),
      optionCards(ctx.store, "beraterId", ADVISORS.map((a) => ({ value: a.id, title: a.label })))
    );
  },
};

/* ---------- 20 · Zusammenfassung ---------- */
export const stepZusammenfassung: Step = {
  id: "zusammenfassung",
  section: "abschluss",
  eyebrow: "Abschluss",
  title: "Zusammenfassung",
  subtitle: "Bitte prüfe deine Angaben vor dem Absenden.",
  validate: (s) => (s.einwilligungen.angabenGeprueft ? [] : ["Bitte bestätige, dass du deine Angaben geprüft hast."]),
  build(ctx) {
    const s = ctx.store;
    const a = s.state;
    const hh = berechneHaushalt(a);
    const ja = (v: string) => (v === "ja" ? "Ja" : v === "nein" ? "Nein" : v === "weiss_nicht" ? "Weiß ich nicht" : "—");

    const sec = (title: string, stepId: string, rows: [string, string][]) =>
      h("div", { class: "summary-section" },
        h("div", { class: "summary-section__head" },
          h("div", { class: "summary-section__title", text: title }),
          h("button", { class: "btn-edit", type: "button", text: "Bearbeiten", on: { click: () => ctx.goTo(stepId) } })),
        h("div", { class: "kv" }, ...rows.map(([k, v]) =>
          h("div", { class: "kv__row" },
            h("span", { class: "kv__k", style: { color: "var(--on-card-muted)" }, text: k }),
            h("span", { class: "kv__v", style: { color: "var(--on-card)" }, text: v || "—" })))));

    const doneDocs = a.dokumente.filter((d) => d.status === "done");

    return h("div", {},
      card(
        sec("Stammdaten", "persoenlich", [
          ["Name", `${a.stammdaten.vorname} ${a.stammdaten.nachname}`],
          ["Geburtsdatum", a.stammdaten.geburtsdatum],
          ["Adresse", `${a.stammdaten.strasse} ${a.stammdaten.hausnummer}, ${a.stammdaten.plz} ${a.stammdaten.ort}`],
          ["E-Mail", a.stammdaten.email],
          ["Mobil", a.stammdaten.mobil],
          ["Familienstand", a.stammdaten.familienstand],
          ["Kinder", a.stammdaten.hatKinder === "ja" ? `Ja (${a.stammdaten.anzahlKinder})` : ja(a.stammdaten.hatKinder)],
        ]),
        sec("Gesundheit", "gesundheit", [
          ["Körperlich", ja(a.gesundheit.koerperlich)],
          ["Psychisch", ja(a.gesundheit.psychisch)],
          ["Raucher", ja(a.gesundheit.raucher)],
          ["Risikoreiche Hobbys", a.gesundheit.risikohobby === "ja" ? `Ja – ${a.gesundheit.risikohobbyText}` : ja(a.gesundheit.risikohobby)],
        ]),
        sec("Vorsorge & Versicherungen", "altersvorsorge", [
          ["Altersvorsorge", `${ja(a.altersvorsorge.vorhanden)} (${a.altersvorsorge.vertraege.length})`],
          ["Arbeitskraft", `${ja(a.arbeitskraft.vorhanden)} (${a.arbeitskraft.vertraege.length})`],
          ["Sachversicherungen", a.sachversicherungen.auswahl.join(", ")],
          ["Vermögen", `${ja(a.vermoegen.vorhanden)} (${a.vermoegen.posten.length} Kategorien)`],
        ]),
        sec("Energie & Krankenkasse", "strom", [
          ["Strom", a.strom.eigenerVertrag === "ja" ? `${a.strom.anbieter} · ${a.strom.abschlag} €` : ja(a.strom.eigenerVertrag)],
          ["Gas", a.gas.eigenerVertrag === "ja" ? `${a.gas.anbieter} · ${a.gas.abschlag} €` : ja(a.gas.eigenerVertrag)],
          ["Krankenkasse", a.krankenversicherung.gesetzlichVersichert === "ja" ? a.krankenversicherung.anbieter : ja(a.krankenversicherung.gesetzlichVersichert)],
        ]),
        sec("Haushaltsrechnung", "haushalt", [
          ["Einnahmen gesamt", formatEUR(hh.einnahmenGesamt)],
          ["Laufende Verbindlichkeiten", formatEUR(hh.laufendeVerbindlichkeiten)],
          [hh.freiVerfuegbar < 0 ? "Monatliche Unterdeckung" : "Frei verfügbar", formatEUR(hh.freiVerfuegbar)],
        ]),
        sec("Zukunft & Haltung", "zukunft", [
          ["Mindestens", `${a.zukunft.mindestens || "—"} €`],
          ["Maximal", `${a.zukunft.komfortabel || "—"} €`],
          ["Einmalig", `${a.zukunft.einmalig || "—"} €`],
          ["Verantwortung", a.verantwortung || "—"],
          ["Risiko", `${a.risiko} / 10`],
          ["Flexibilität", `${a.flexibilitaet} / 10`],
        ]),
        sec("Dokumente & Berater", "dokumente", [
          ["Hochgeladen", doneDocs.length ? doneDocs.map((d) => d.kategorie).join(", ") : "keine"],
          ["Berater", advisorLabel(a.beraterId)],
        ])
      ),
      card(
        checkbox(s, "einwilligungen.angabenGeprueft",
          "Ich habe meine Angaben geprüft und nach bestem Wissen vollständig und richtig gemacht.")
      )
    );
  },
};

/* ---------- 21 · Datenschutz & Absenden ---------- */
export const stepDatenschutz: Step = {
  id: "datenschutz",
  section: "abschluss",
  eyebrow: "Abschluss",
  title: "Datenschutz & Einwilligung",
  subtitle: "Vor dem Absenden brauchen wir deine Zustimmung.",
  validate: vEinwilligungen,
  build(ctx) {
    const s = ctx.store;

    return h("div", {},
      card(
        h("div", { class: "card__title", text: "Deine Einwilligungen" }),
        checkbox(s, "einwilligungen.datenschutz", linkLabel("Ich habe die ", "Datenschutzerklärung", "/legal/datenschutz.html", " zur Kenntnis genommen.")),
        checkbox(s, "einwilligungen.verarbeitung", "Ich stimme der Verarbeitung meiner Angaben zur Vorbereitung meiner Beratung zu."),
        checkbox(s, "einwilligungen.uebermittlung", "Ich stimme der Übermittlung meiner Angaben an die Blacksmith Finance GmbH zu."),
        checkbox(s, "einwilligungen.weiterleitung", "Ich stimme der Weiterleitung an meinen ausgewählten Berater zu."),
        checkbox(s, "einwilligungen.driveSpeicherung", "Ich stimme der Speicherung meiner hochgeladenen Dokumente im geschützten Google Drive zu."),
        checkbox(s, "einwilligungen.gesundheit",
          "Ich willige ausdrücklich ein, dass meine Ja-/Nein-Angaben zu körperlicher und psychischer Gesundheit verarbeitet werden (besondere Kategorie personenbezogener Daten).", true)
      ),
      h("p", { class: "field__optional", style: { textAlign: "center" } },
        linkLabel("", "Impressum", "/legal/impressum.html", "  ·  "),
      )
    );
  },
};

function linkLabel(pre: string, linkText: string, href: string, post: string): HTMLElement {
  const span = h("span", {}, pre);
  span.appendChild(h("a", { href, text: linkText, attrs: { target: "_blank", rel: "noopener" } }));
  span.appendChild(document.createTextNode(post));
  // zweiter Link (Impressum) für Datenschutz-Zeile
  return span;
}
